import { useSetMaintenanceMode } from "../errorHandling/useSetMaintenanceMode";

import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";

interface EasySessionContextType {
  isEasySessionInit: boolean;
  isLoggedIntoEasy: boolean;
  setIsLoggedIntoEasy: (value: boolean) => void;
  preferredName: string;
  checkLogin: () => Promise<void>;
}

const EasySessionContext = createContext<EasySessionContextType | undefined>(
  undefined,
);

interface EasySessionLoadingContextType {
  isFetchingEasyLoginStatus: boolean;
}

const EasySessionLoadingContext = createContext<
  EasySessionLoadingContextType | undefined
>(undefined);

interface EasySessionProviderProps {
  children: ReactNode;
}

export const EasySessionProvider: React.FC<EasySessionProviderProps> = ({
  children,
}) => {
  const [isLoggedIntoEasy, setIsLoggedIntoEasy] = useState<boolean>(false);
  const [isEasySessionInit, setIsEasySessionInit] = useState<boolean>(true);
  const [isFetchingEasyLoginStatus, setIsFetchingEasyLoginStatus] =
    useState<boolean>(false);
  const [preferredName, setPreferredName] = useState<string>("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const updateMaintenanceMode = useSetMaintenanceMode();
  /*  const { data: easyLogin, refetch: refetchEasyLoginState } =
    useGetApiEasyCheckLoginState(); */

  const checkLogin = useCallback(async (): Promise<void> => {
    console.log("checkLogin");

    setIsFetchingEasyLoginStatus(true);
    try {
      const response = await fetch("/api/Easy/CheckLoginState", {
        method: "GET",
      });

      if (response.status === 200) {
        const data = await response.json();
        if (typeof data?.string === "string" && data.string !== preferredName) {
          setPreferredName(data.string);
        }
        if (!isLoggedIntoEasy) {
          setIsLoggedIntoEasy(true);
        }
        // The backend answered normally — this poll runs unconditionally
        // every 60s (see the interval below), so it doubles as the recovery
        // signal for the maintenance-mode overlay set from a 503 below.
        updateMaintenanceMode(false);
      } else if (response.status === 401) {
        if (isLoggedIntoEasy) {
          setIsLoggedIntoEasy(false);
        }
        // Not logged into EASY is a normal, common state — not an outage —
        // but the backend still answered coherently, so recovery holds.
        updateMaintenanceMode(false);
      } else {
        const error = await response.text();
        console.warn(error);
        setIsLoggedIntoEasy(false);
        if (response.status === 503) {
          updateMaintenanceMode(true);
        }
      }
    } catch (error) {
      console.warn(error);
      if (isLoggedIntoEasy) {
        setIsLoggedIntoEasy(false);
      }
    } finally {
      if (isEasySessionInit) {
        console.log("finalize easy init");
        setIsEasySessionInit(false);
        // setTimeout(() => setIsEasySessionInit(false), 5000); //TODO Remove
      }
      setIsFetchingEasyLoginStatus(false);
    }
  }, [
    isEasySessionInit,
    isLoggedIntoEasy,
    preferredName,
    updateMaintenanceMode,
  ]);
  const checkLoginEffectEvent = useEffectEvent(() => {
    checkLogin();
  });

  useEffect(() => {
    console.log("easy session provider effect");
    // eslint-disable-next-line react-hooks/set-state-in-effect
    checkLoginEffectEvent();
    intervalRef.current = setInterval(async () => {
      console.log("interval", typeof checkLoginEffectEvent);

      checkLoginEffectEvent();
    }, 60000); //previously 300000

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const loadingValue = useMemo(
    () => ({ isFetchingEasyLoginStatus }),
    [isFetchingEasyLoginStatus],
  );

  const sessionValue = useMemo(
    () => ({
      isEasySessionInit,
      isLoggedIntoEasy,
      setIsLoggedIntoEasy,
      preferredName,
      checkLogin,
    }),
    [checkLogin, isEasySessionInit, isLoggedIntoEasy, preferredName],
  );

  return (
    <EasySessionLoadingContext.Provider value={loadingValue}>
      <EasySessionContext.Provider value={sessionValue}>
        {children}
      </EasySessionContext.Provider>
    </EasySessionLoadingContext.Provider>
  );
};

export const useEasySession = (): EasySessionContextType => {
  const context = useContext(EasySessionContext);
  if (!context) {
    throw new Error("useEasySession must be used within a EasySessionProvider");
  }
  return context;
};

export const useEasySessionLoading = (): EasySessionLoadingContextType => {
  const context = useContext(EasySessionLoadingContext);
  if (!context) {
    throw new Error(
      "useEasySessionLoading must be used within a EasySessionProvider",
    );
  }
  return context;
};
